/**
 * Tabellen im Signatur-/Kampagnen-Editor.
 *
 * Enthält das Einfügen neuer Tabellen, die Struktur-Operationen (Zeile/Spalte
 * hinzufügen oder entfernen) und den Format-Dialog für Zelle, Zeile, Spalte
 * oder die ganze Tabelle. Der Dialog liest die Werte der Cursorstelle bzw. der
 * ersten Zelle des Bereichs nur aus; angewendet wird erst mit OK.
 *
 * Erzeugt bewusst E-Mail-taugliches HTML: Inline-Styles plus die klassischen
 * Tabellen-Attribute, die Outlook Desktop zusätzlich auswertet.
 */

import { cssColorToHex, parseCssColor } from "../utils/cssColor";
import { createSearchableDropdown } from "./editor-searchlist";
import { normalizeSizeInput, parseIntegerInput } from "../utils/editorInputs";

/** Grenzen der Zahlenfelder; identisch in Dialog-Prüfung und Anwendung. */
const LIMITS = {
  rows: { min: 1, max: 50 },
  columns: { min: 1, max: 20 },
  borderWidth: { min: 0, max: 20 },
  padding: { min: 0, max: 60 },
  fontSize: { min: 1, max: 200 },
} as const;

export type TableScope = "cell" | "row" | "column" | "table";

export interface TableEditorDeps {
  t: (key: string) => string;
  /** Farbwähler des Editors. Darf die Auswahl im Editor nicht verändern. */
  pickColor: (titleKey: string, current: string, isBackground: boolean, onPick: (value: string) => void) => void;
  /** Schriftarten der Editor-Toolbar (erster Eintrag = Standard). */
  fontOptions: { label: string; value: string }[];
  /** Nach DOM-Änderungen aufrufen, damit z. B. die Längenanzeige aktualisiert wird. */
  notifyChange: () => void;
}

export interface TableContext {
  table: HTMLTableElement;
  row: HTMLTableRowElement;
  cell: HTMLTableCellElement;
}

interface LogicalCell {
  cell: HTMLTableCellElement;
  row: number;
  column: number;
  rowSpan: number;
  columnSpan: number;
}

interface LogicalTable {
  grid: (HTMLTableCellElement | undefined)[][];
  cells: Map<HTMLTableCellElement, LogicalCell>;
}

/** Rechteckige Zellauswahl unabhängig von der nativen Textmarkierung. */
export interface TableCellSelection {
  table: HTMLTableElement;
  anchor: HTMLTableCellElement;
  focus: HTMLTableCellElement;
  cells: HTMLTableCellElement[];
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const DEFAULT_BORDER_COLOR = "#cccccc";
const DEFAULT_TEXT_COLOR = "#000000";
/** Kein Hintergrund = Farbe ohne Deckkraft; so lässt es sich im Farbdialog einstellen. */
const TRANSPARENT_COLOR = "rgba(255,255,255,0)";
const BORDER_STYLES = ["solid", "dashed", "dotted", "double", "none"];

/** Tabellenkontext an der aktuellen Cursorposition; null außerhalb einer Tabelle. */
export function findTableContext(body: HTMLElement): TableContext | null {
  const sel = window.getSelection();
  const node = sel && sel.rangeCount > 0 ? (sel.focusNode ?? sel.anchorNode) : null;
  if (!node || !body.contains(node)) return null;
  const start = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  const cell = start?.closest("td,th") as HTMLTableCellElement | null;
  if (!cell || !body.contains(cell)) return null;
  const row = cell.closest("tr");
  const table = cell.closest("table");
  if (!row || !table || !body.contains(table)) return null;
  return { table, row, cell };
}

/** Setzt den Cursor an den Anfang einer Zelle. */
export function placeCaretInCell(cell: HTMLTableCellElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(cell);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

export interface TableInsertOptions {
  rows: number;
  columns: number;
  /** z. B. "100%" oder "480px"; leer = keine Breitenangabe. */
  width: string;
  borderWidth: number;
  borderStyle: string;
  borderColor: string;
  padding: number;
  headerRow: boolean;
}

/** Baut die Tabelle als DOM-Knoten (gleiches Markup wie buildTableHtml). */
export function buildTableElement(options: TableInsertOptions): HTMLTableElement {
  const host = document.createElement("div");
  host.innerHTML = buildTableHtml(options);
  const table = host.querySelector("table");
  if (table) return table as HTMLTableElement;
  // Sollte nie eintreten; leere Tabelle ist besser als ein Absturz.
  return document.createElement("table");
}

/**
 * Fügt eine Tabelle an der Cursorposition ein.
 *
 * Bevorzugt execCommand, weil das die native Rückgängig-Funktion bedient.
 * Manche Office-Hosts führen den Befehl aber nicht aus; deshalb wird das
 * Ergebnis geprüft und die Tabelle notfalls direkt ins DOM gehängt.
 */
export function insertTableAtCaret(body: HTMLElement, options: TableInsertOptions): HTMLTableElement | null {
  const html = buildTableHtml(options);
  const before = new Set(Array.from(body.querySelectorAll("table")));
  const findNew = (): HTMLTableElement | null =>
    (Array.from(body.querySelectorAll("table")).find((t) => !before.has(t)) as HTMLTableElement | undefined) ?? null;

  try {
    document.execCommand("insertHTML", false, html);
  } catch (e) {
    console.warn("[SunnySign] execCommand insertHTML nicht verfügbar:", e);
  }
  let table = findNew();
  if (!table) {
    console.warn("[SunnySign] Tabelle wurde per execCommand nicht eingefügt — direkter DOM-Einbau.");
    table = buildTableElement(options);
    insertBlockAtCaret(body, table);
    if (!body.contains(table)) return null;
  }
  // Ohne Folgeknoten kommt man hinter der Tabelle nicht mehr in den Fließtext.
  if (!table.nextSibling) table.parentNode?.appendChild(document.createElement("br"));
  const firstCell = table.rows[0]?.cells[0];
  if (firstCell) placeCaretInCell(firstCell);
  return table;
}

/**
 * Hängt ein Blockelement hinter den Absatz, in dem der Cursor steht — innerhalb
 * einer Tabellenzelle hinter den dortigen Absatz. Ohne Cursor ans Ende.
 */
function insertBlockAtCaret(body: HTMLElement, block: HTMLElement): void {
  const sel = window.getSelection();
  const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
  if (!range || !body.contains(range.startContainer)) {
    body.appendChild(block);
    return;
  }
  const start = range.startContainer;
  if (start === body) {
    body.insertBefore(block, body.childNodes[range.startOffset] ?? null);
    return;
  }
  const isHost = (node: Node | null): boolean =>
    !!node && (node === body || (node.nodeType === Node.ELEMENT_NODE && /^(?:TD|TH)$/.test((node as Element).tagName)));
  let node: Node = start;
  while (node.parentNode && !isHost(node.parentNode) && node.parentNode !== body) {
    node = node.parentNode;
  }
  const parent = node.parentNode ?? body;
  parent.insertBefore(block, node.nextSibling);
}

/** Baut E-Mail-taugliches Tabellen-HTML (Inline-Styles + Outlook-Attribute). */
export function buildTableHtml(options: TableInsertOptions): string {
  const rows = Math.max(1, Math.min(50, Math.round(options.rows)));
  const columns = Math.max(1, Math.min(20, Math.round(options.columns)));
  const padding = Math.max(0, Math.min(60, Math.round(options.padding)));
  const borderWidth = Math.max(0, Math.min(20, Math.round(options.borderWidth)));
  const borderStyle = BORDER_STYLES.includes(options.borderStyle) ? options.borderStyle : "solid";
  const hasBorder = borderWidth > 0 && borderStyle !== "none";
  const cellBorder = hasBorder ? `border:${borderWidth}px ${borderStyle} ${options.borderColor};` : "";
  const cellStyle = `${cellBorder}padding:${padding}px;vertical-align:top;`;
  const headerStyle = `${cellStyle}background-color:#f2f2f2;font-weight:bold;text-align:left;`;

  const tableStyle = `border-collapse:collapse;${options.width ? `width:${options.width};` : ""}`;
  const parts: string[] = [
    `<table cellpadding="0" cellspacing="0" border="0" style="${tableStyle}"><tbody>`,
  ];
  for (let r = 0; r < rows; r++) {
    const isHeader = options.headerRow && r === 0;
    parts.push("<tr>");
    for (let c = 0; c < columns; c++) {
      const tag = isHeader ? "th" : "td";
      parts.push(`<${tag} style="${isHeader ? headerStyle : cellStyle}"><br></${tag}>`);
    }
    parts.push("</tr>");
  }
  parts.push("</tbody></table>");
  return parts.join("");
}

/**
 * Dialog zum Einfügen einer Tabelle. Liefert das fertige HTML an den Aufrufer,
 * der es an der Cursorposition einfügt.
 */
export function openTableInsertDialog(
  deps: TableEditorDeps,
  onInsert: (options: TableInsertOptions) => void
): void {
  const t = deps.t;
  const overlay = document.createElement("div");
  // Die Overlay-Klasse bringt die Abdunklung schon mit; ein zusätzliches
  // Backdrop-Element würde sie ein zweites Mal auftragen.
  overlay.className = "sunnysign-editor-dialog-overlay sunnysign-table-dialog";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const box = document.createElement("div");
  box.className = "sunnysign-editor-dialog-box sunnysign-table-dialog__box";

  const title = document.createElement("p");
  title.className = "sunnysign-editor-color-dialog__title";
  title.textContent = t("table.insertTitle");

  const rowsInput = createNumberInput("2", t("table.rows"));
  const columnsInput = createNumberInput("2", t("table.columns"));
  const widthControl = createSizeControl("100%", t("table.width"), t("table.widthUnit"));
  const errorEl = createDialogError();
  const borderWidthInput = createNumberInput("1", t("table.borderWidth"));
  const borderStyleSelect = createSelectControl(
    BORDER_STYLES.map((s) => ({ label: t(`table.borderStyle.${s}`), value: s })),
    "solid",
    t("table.borderStyle")
  );
  const borderColor = createColorButton(deps, "table.borderColor", DEFAULT_BORDER_COLOR, false);
  const paddingInput = createNumberInput("6", t("table.padding"));
  const headerCheckbox = document.createElement("input");
  headerCheckbox.type = "checkbox";
  headerCheckbox.className = "sunnysign-table-dialog__checkbox";
  headerCheckbox.setAttribute("aria-label", t("table.headerRow"));

  const actions = document.createElement("div");
  actions.className = "sunnysign-form-actions";
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "sunnysign-btn sunnysign-btn--primary";
  okBtn.textContent = t("editor.colorOk");
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "sunnysign-btn sunnysign-btn--secondary";
  cancelBtn.textContent = t("form.cancel");
  actions.appendChild(okBtn);
  actions.appendChild(cancelBtn);

  box.appendChild(title);
  box.appendChild(labelledRow(t("table.rows"), rowsInput));
  box.appendChild(labelledRow(t("table.columns"), columnsInput));
  box.appendChild(labelledRow(t("table.width"), widthControl.element));
  box.appendChild(labelledRow(t("table.borderWidth"), borderWidthInput));
  box.appendChild(labelledRow(t("table.borderStyle"), borderStyleSelect));
  box.appendChild(labelledRow(t("table.borderColor"), borderColor.element));
  box.appendChild(labelledRow(t("table.padding"), paddingInput));
  box.appendChild(labelledRow(t("table.headerRow"), headerCheckbox));
  clearErrorOnInput(errorEl, [rowsInput, columnsInput, widthControl.input, borderWidthInput, paddingInput]);
  box.appendChild(errorEl);
  box.appendChild(actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();
  cancelBtn.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  okBtn.addEventListener("click", () => {
    const rows = parseIntegerInput(rowsInput.value, LIMITS.rows.min, LIMITS.rows.max);
    const columns = parseIntegerInput(columnsInput.value, LIMITS.columns.min, LIMITS.columns.max);
    const width = widthControl.get();
    const borderWidth = parseIntegerInput(
      borderWidthInput.value,
      LIMITS.borderWidth.min,
      LIMITS.borderWidth.max
    );
    const padding = parseIntegerInput(paddingInput.value, LIMITS.padding.min, LIMITS.padding.max);
    const problem =
      rows === null
        ? { key: "table.errorRows", field: rowsInput }
        : columns === null
          ? { key: "table.errorColumns", field: columnsInput }
          : width === null
            ? { key: "table.errorWidth", field: widthControl.input }
            : borderWidth === null
              ? { key: "table.errorBorderWidth", field: borderWidthInput }
              : padding === null
                ? { key: "table.errorPadding", field: paddingInput }
                : null;
    if (problem) {
      showDialogError(errorEl, t(problem.key));
      problem.field.focus();
      return;
    }
    close();
    onInsert({
      rows: rows as number,
      columns: columns as number,
      width: width as string,
      borderWidth: borderWidth as number,
      borderStyle: borderStyleSelect.value,
      borderColor: borderColor.get(),
      padding: padding as number,
      headerRow: headerCheckbox.checked,
    });
  });
  rowsInput.focus();
}

/** Fügt eine Zeile über oder unter der Zeile des Cursors ein. */
export function insertTableRow(ctx: TableContext, where: "above" | "below"): void {
  const section = ctx.row.parentElement;
  if (!section) return;
  const newRow = document.createElement("tr");
  Array.from(ctx.row.cells).forEach((source) => {
    const cell = document.createElement("td");
    const style = source.getAttribute("style");
    // Kopfzeilen-Optik nicht in die neue Datenzeile übernehmen.
    if (style) cell.setAttribute("style", source.tagName === "TH" ? style.replace(/font-weight\s*:[^;]*;?/gi, "") : style);
    const colspan = source.getAttribute("colspan");
    if (colspan) cell.setAttribute("colspan", colspan);
    cell.appendChild(document.createElement("br"));
    newRow.appendChild(cell);
  });
  section.insertBefore(newRow, where === "above" ? ctx.row : ctx.row.nextSibling);
  const target = newRow.cells[0];
  if (target) placeCaretInCell(target);
}

/** Fügt eine Spalte links oder rechts der Cursor-Spalte ein. */
export function insertTableColumn(ctx: TableContext, where: "left" | "right"): void {
  const index = ctx.cell.cellIndex;
  Array.from(ctx.table.rows).forEach((row) => {
    const reference = row.cells[index];
    const isHeader = reference ? reference.tagName === "TH" : row.cells[0]?.tagName === "TH";
    const cell = document.createElement(isHeader ? "th" : "td");
    const style = (reference ?? row.cells[row.cells.length - 1])?.getAttribute("style");
    if (style) cell.setAttribute("style", style);
    cell.appendChild(document.createElement("br"));
    if (!reference) row.appendChild(cell);
    else row.insertBefore(cell, where === "left" ? reference : reference.nextSibling);
  });
  const created = ctx.row.cells[where === "left" ? index : index + 1];
  if (created) placeCaretInCell(created);
}

/** Entfernt die Zeile des Cursors; bei der letzten Zeile die ganze Tabelle. */
export function deleteTableRow(ctx: TableContext): void {
  if (ctx.table.rows.length <= 1) {
    deleteTable(ctx);
    return;
  }
  const fallback = (ctx.row.nextElementSibling ?? ctx.row.previousElementSibling) as HTMLTableRowElement | null;
  ctx.row.remove();
  const target = fallback?.cells[0];
  if (target) placeCaretInCell(target);
}

/** Entfernt die Spalte des Cursors; bei der letzten Spalte die ganze Tabelle. */
export function deleteTableColumn(ctx: TableContext): void {
  const index = ctx.cell.cellIndex;
  const maxCells = Math.max(...Array.from(ctx.table.rows).map((r) => r.cells.length));
  if (maxCells <= 1) {
    deleteTable(ctx);
    return;
  }
  Array.from(ctx.table.rows).forEach((row) => row.cells[index]?.remove());
  const target = ctx.row.cells[Math.min(index, ctx.row.cells.length - 1)];
  if (target) placeCaretInCell(target);
}

/** Entfernt die ganze Tabelle. */
export function deleteTable(ctx: TableContext): void {
  ctx.table.remove();
}

/**
 * Baut die sichtbaren Tabellenkoordinaten auf. Anders als cellIndex beachtet
 * dieses Raster bereits verbundene Zellen mit rowspan/colspan.
 */
function logicalTable(table: HTMLTableElement): LogicalTable {
  const grid: (HTMLTableCellElement | undefined)[][] = [];
  const cells = new Map<HTMLTableCellElement, LogicalCell>();
  Array.from(table.rows).forEach((row, rowIndex) => {
    grid[rowIndex] ??= [];
    let column = 0;
    Array.from(row.cells).forEach((cell) => {
      while (grid[rowIndex][column]) column += 1;
      const rowSpan = Math.max(1, cell.rowSpan || 1);
      const columnSpan = Math.max(1, cell.colSpan || 1);
      const placement = { cell, row: rowIndex, column, rowSpan, columnSpan };
      cells.set(cell, placement);
      for (let r = rowIndex; r < rowIndex + rowSpan; r += 1) {
        grid[r] ??= [];
        for (let c = column; c < column + columnSpan; c += 1) {
          grid[r][c] = cell;
        }
      }
      column += columnSpan;
    });
  });
  return { grid, cells };
}

/**
 * Liefert das ausgefüllte Rechteck zwischen Anker- und Zielzelle. Schneidet
 * das Rechteck eine bereits verbundene Zelle nur teilweise, ist es ungültig.
 */
export function getTableCellSelection(
  anchor: HTMLTableCellElement,
  focus: HTMLTableCellElement
): TableCellSelection | null {
  const table = anchor.closest("table");
  if (!table || focus.closest("table") !== table) return null;
  const layout = logicalTable(table);
  const start = layout.cells.get(anchor);
  const end = layout.cells.get(focus);
  if (!start || !end) return null;

  const top = Math.min(start.row, end.row);
  const bottom = Math.max(start.row + start.rowSpan - 1, end.row + end.rowSpan - 1);
  const left = Math.min(start.column, end.column);
  const right = Math.max(start.column + start.columnSpan - 1, end.column + end.columnSpan - 1);
  const selected = new Set<HTMLTableCellElement>();

  for (let row = top; row <= bottom; row += 1) {
    for (let column = left; column <= right; column += 1) {
      const cell = layout.grid[row]?.[column];
      if (!cell) return null;
      selected.add(cell);
    }
  }
  for (const cell of selected) {
    const placement = layout.cells.get(cell);
    if (
      !placement ||
      placement.row < top ||
      placement.column < left ||
      placement.row + placement.rowSpan - 1 > bottom ||
      placement.column + placement.columnSpan - 1 > right
    ) {
      return null;
    }
  }

  const cells = Array.from(selected).sort((a, b) => {
    const aa = layout.cells.get(a)!;
    const bb = layout.cells.get(b)!;
    return aa.row - bb.row || aa.column - bb.column;
  });
  return { table, anchor, focus, cells, top, bottom, left, right };
}

export function isMergedTableCell(cell: HTMLTableCellElement): boolean {
  return cell.rowSpan > 1 || cell.colSpan > 1;
}

function cellHasContent(cell: HTMLTableCellElement): boolean {
  const text = cell.textContent?.replace(/\u00a0/g, " ").trim() ?? "";
  return !!text || !!cell.querySelector("img,table,hr");
}

/** Verbindet eine gültige rechteckige Auswahl; die Zelle links oben bleibt erhalten. */
export function mergeTableCells(selection: TableCellSelection): HTMLTableCellElement | null {
  if (selection.cells.length < 2 || !selection.table.isConnected) return null;
  // Noch einmal gegen den aktuellen DOM prüfen: zwischen Auswahl und Klick
  // könnte eine andere Tabellenaktion die Zellstruktur verändert haben.
  const current = getTableCellSelection(selection.anchor, selection.focus);
  if (!current || current.cells.length < 2) return null;

  const keeper = current.cells[0];
  let keeperHasContent = cellHasContent(keeper);
  if (!keeperHasContent) keeper.replaceChildren();
  for (const cell of current.cells.slice(1)) {
    if (cellHasContent(cell)) {
      if (keeperHasContent) keeper.appendChild(document.createElement("br"));
      while (cell.firstChild) keeper.appendChild(cell.firstChild);
      keeperHasContent = true;
    }
    cell.remove();
  }
  if (!keeperHasContent) keeper.appendChild(document.createElement("br"));

  const rowSpan = current.bottom - current.top + 1;
  const columnSpan = current.right - current.left + 1;
  if (rowSpan > 1) keeper.setAttribute("rowspan", String(rowSpan));
  else keeper.removeAttribute("rowspan");
  if (columnSpan > 1) keeper.setAttribute("colspan", String(columnSpan));
  else keeper.removeAttribute("colspan");
  placeCaretInCell(keeper);
  return keeper;
}

/**
 * Teilt eine verbundene Zelle wieder in einzelne Zellen. Formatierung wird
 * übernommen; Breite/Höhe werden entfernt, weil sie am verbundenen Element
 * die Gesamtgröße und nicht die Größe einer einzelnen Zielzelle beschrieben.
 */
export function splitTableCell(cell: HTMLTableCellElement): HTMLTableCellElement[] {
  if (!isMergedTableCell(cell)) return [];
  const table = cell.closest("table");
  if (!table) return [];
  const layout = logicalTable(table);
  const placement = layout.cells.get(cell);
  if (!placement) return [];

  const rows = Array.from(table.rows);
  const logicalColumns = new Map<HTMLTableCellElement, number>();
  layout.cells.forEach((entry, existing) => logicalColumns.set(existing, entry.column));
  const style = cell.getAttribute("style");
  const tagName = cell.tagName.toLowerCase();
  const result: HTMLTableCellElement[] = [cell];

  cell.removeAttribute("rowspan");
  cell.removeAttribute("colspan");
  cell.style.removeProperty("width");
  cell.style.removeProperty("height");
  cell.removeAttribute("width");
  cell.removeAttribute("height");

  for (let rowIndex = placement.row; rowIndex < placement.row + placement.rowSpan; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!row) continue;
    for (
      let column = placement.column;
      column < placement.column + placement.columnSpan;
      column += 1
    ) {
      if (rowIndex === placement.row && column === placement.column) continue;
      const created = document.createElement(tagName) as HTMLTableCellElement;
      if (style) created.setAttribute("style", style);
      created.style.removeProperty("width");
      created.style.removeProperty("height");
      created.appendChild(document.createElement("br"));
      logicalColumns.set(created, column);
      const before = Array.from(row.cells).find(
        (existing) => existing !== cell && (logicalColumns.get(existing) ?? Number.MAX_SAFE_INTEGER) > column
      );
      row.insertBefore(created, before ?? null);
      result.push(created);
    }
  }
  placeCaretInCell(cell);
  return result;
}

/** Zellen, auf die der gewählte Bereich wirkt. */
function collectScopeCells(scope: TableScope, ctx: TableContext): HTMLTableCellElement[] {
  if (scope === "cell") return [ctx.cell];
  if (scope === "row") return Array.from(ctx.row.cells);
  if (scope === "column") {
    const index = ctx.cell.cellIndex;
    return Array.from(ctx.table.rows)
      .map((row) => row.cells[index])
      .filter((cell): cell is HTMLTableCellElement => !!cell);
  }
  return Array.from(ctx.table.rows).flatMap((row) => Array.from(row.cells));
}

/** Markierungsklasse der Vorschau; wird nie gespeichert (siehe clearScopePreview). */
const PREVIEW_CLASS = "sunnysign-cell-preview";
let previewedCells: HTMLTableCellElement[] = [];

/**
 * Hebt beim Überfahren eines Bereichs-Buttons die betroffenen Zellen hervor.
 * Bewusst nur über eine Klasse: die Hintergrundfarbe der Zellen bleibt im
 * Inline-Style unangetastet, damit der Dialog später die echten Werte liest.
 */
export function previewScopeCells(scope: TableScope, ctx: TableContext): void {
  clearScopePreview();
  previewedCells = collectScopeCells(scope, ctx);
  for (const cell of previewedCells) cell.classList.add(PREVIEW_CLASS);
}

/** Nimmt die Vorschau zurück und hinterlässt kein leeres class-Attribut. */
export function clearScopePreview(): void {
  for (const cell of previewedCells) {
    cell.classList.remove(PREVIEW_CLASS);
    if (!cell.className.trim()) cell.removeAttribute("class");
  }
  previewedCells = [];
}

/** Kanten, die im Rahmen-Detail-Dialog einzeln geschaltet werden. */
type BorderEdge = "top" | "bottom" | "left" | "right" | "insideVertical" | "insideHorizontal";
/** Anzeigereihenfolge im Dialog; nicht verfügbare Kanten werden gesperrt gezeigt. */
const ALL_BORDER_EDGES: BorderEdge[] = [
  "top",
  "bottom",
  "left",
  "right",
  "insideVertical",
  "insideHorizontal",
];
type BorderSide = "top" | "right" | "bottom" | "left";
interface BorderTarget {
  cell: HTMLTableCellElement;
  side: BorderSide;
}
/** „keep“ lässt die Kante unangetastet – wichtig bei gemischten Rahmen. */
type EdgeState = "on" | "off" | "keep";

/**
 * Zellen des Bereichs als Raster. Erst die Zeilen-/Spaltenlage macht klar,
 * welche Zellseiten außen liegen und welche innen zwischen zwei Zellen.
 */
function scopeGrid(scope: TableScope, ctx: TableContext): HTMLTableCellElement[][] {
  if (scope === "cell") return [[ctx.cell]];
  if (scope === "row") return [Array.from(ctx.row.cells)];
  if (scope === "column") {
    const index = ctx.cell.cellIndex;
    return Array.from(ctx.table.rows)
      .map((row) => row.cells[index])
      .filter((cell): cell is HTMLTableCellElement => !!cell)
      .map((cell) => [cell]);
  }
  return Array.from(ctx.table.rows)
    .map((row) => Array.from(row.cells))
    .filter((row) => row.length > 0);
}

/** Kanten, die im aktuellen Bereich überhaupt vorkommen. */
function availableEdges(grid: HTMLTableCellElement[][]): BorderEdge[] {
  const edges: BorderEdge[] = ["top", "bottom", "left", "right"];
  if (grid.some((row) => row.length > 1)) edges.push("insideVertical");
  if (grid.length > 1) edges.push("insideHorizontal");
  return edges;
}

/**
 * Zellseiten, die zu einer Kante gehören. Innenkanten setzen beide beteiligten
 * Seiten, weil bei border-collapse sonst der Nachbar die Linie überstimmt.
 */
function borderTargets(edge: BorderEdge, grid: HTMLTableCellElement[][]): BorderTarget[] {
  const rows = grid.filter((row) => row.length > 0);
  if (rows.length === 0) return [];
  const targets: BorderTarget[] = [];
  switch (edge) {
    case "top":
      rows[0].forEach((cell) => targets.push({ cell, side: "top" }));
      break;
    case "bottom":
      rows[rows.length - 1].forEach((cell) => targets.push({ cell, side: "bottom" }));
      break;
    case "left":
      rows.forEach((row) => targets.push({ cell: row[0], side: "left" }));
      break;
    case "right":
      rows.forEach((row) => targets.push({ cell: row[row.length - 1], side: "right" }));
      break;
    case "insideVertical":
      rows.forEach((row) =>
        row.forEach((cell, index) => {
          if (index < row.length - 1) targets.push({ cell, side: "right" });
          if (index > 0) targets.push({ cell, side: "left" });
        })
      );
      break;
    case "insideHorizontal":
      rows.forEach((row, rowIndex) =>
        row.forEach((cell) => {
          if (rowIndex < rows.length - 1) targets.push({ cell, side: "bottom" });
          if (rowIndex > 0) targets.push({ cell, side: "top" });
        })
      );
      break;
  }
  return targets;
}

function applyEdgeBorder(targets: BorderTarget[], value: string): void {
  for (const { cell, side } of targets) cell.style.setProperty(`border-${side}`, value);
}

/** Zelle, deren Formatierung im Dialog vorbelegt wird. */
function referenceCell(scope: TableScope, ctx: TableContext): HTMLTableCellElement {
  if (scope === "row") return ctx.row.cells[0] ?? ctx.cell;
  if (scope === "column") return ctx.table.rows[0]?.cells[ctx.cell.cellIndex] ?? ctx.cell;
  if (scope === "table") return ctx.table.rows[0]?.cells[0] ?? ctx.cell;
  return ctx.cell;
}

interface TableFormatValues {
  padding: string;
  background: string;
  /** "" = keine Hintergrundfarbe gesetzt. */
  backgroundSet: boolean;
  color: string;
  fontFamily: string;
  fontSize: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  textAlign: string;
  verticalAlign: string;
  width: string;
  height: string;
}

/**
 * Eine HTML-Zeile hat keine eigene Breite. Sie wird durch Tabelle und Spalten
 * bestimmt; eine Zeilenbreite darf daher weder auf alle Zellen noch
 * überraschend auf die ganze Tabelle angewandt werden.
 */
function collectWidthTargets(scope: TableScope, ctx: TableContext): HTMLElement[] {
  if (scope === "table") return [ctx.table];
  if (scope === "row") return [];
  return collectScopeCells(scope, ctx);
}

/** Träger der Höhe; bei einer Zeile zusätzlich das <tr>, das Outlook auswertet. */
function collectHeightTargets(scope: TableScope, ctx: TableContext): HTMLElement[] {
  if (scope === "table") return [ctx.table];
  if (scope === "row") return [ctx.row, ...collectScopeCells(scope, ctx)];
  return collectScopeCells(scope, ctx);
}

/**
 * Liest die aktuell wirksame Formatierung der Referenzzelle (nur lesend).
 * Die Werte entsprechen exakt dem, was die Dialogfelder anzeigen — nur so
 * lässt sich später erkennen, welche Felder der Nutzer wirklich geändert hat.
 */
function readFormat(
  scope: TableScope,
  ctx: TableContext,
  fontOptions: { label: string; value: string }[]
): TableFormatValues {
  const cell = referenceCell(scope, ctx);
  const computed = window.getComputedStyle(cell);
  const ownBackground = parseCssColor(computed.backgroundColor);
  const widthSource = collectWidthTargets(scope, ctx)[0] ?? null;
  const heightSource = scope === "row" ? ctx.row : (collectHeightTargets(scope, ctx)[0] ?? cell);
  return {
    padding: String(Math.round(parseFloat(computed.paddingTop) || 0)),
    background: cssColorToHex(computed.backgroundColor, "#ffffff"),
    backgroundSet: !!ownBackground && ownBackground.a > 0,
    color: cssColorToHex(computed.color, DEFAULT_TEXT_COLOR),
    fontFamily: matchFontOption(fontOptions, computed.fontFamily),
    fontSize: String(Math.round(parseFloat(computed.fontSize) || 0)),
    bold: (parseInt(computed.fontWeight, 10) || 400) >= 600,
    italic: computed.fontStyle === "italic" || computed.fontStyle === "oblique",
    underline: `${computed.textDecorationLine || computed.textDecoration || ""}`.includes("underline"),
    textAlign: ["left", "center", "right"].includes(computed.textAlign) ? computed.textAlign : "left",
    verticalAlign: ["top", "middle", "bottom"].includes(computed.verticalAlign) ? computed.verticalAlign : "top",
    width: widthSource ? readSize(widthSource, "width") : "",
    height: readSize(heightSource, "height"),
  };
}

/**
 * Vorbelegung des Rahmen-Dialogs aus der Referenzzelle. Ohne sichtbaren Rahmen
 * startet er mit 1px solid, damit ein Klick auf „Linie“ auch etwas zeichnet.
 */
function readBorderPreset(scope: TableScope, ctx: TableContext): { width: string; style: string; color: string } {
  const computed = window.getComputedStyle(referenceCell(scope, ctx));
  const style = computed.borderTopStyle || "none";
  const width = Math.round(parseFloat(computed.borderTopWidth) || 0);
  const visible = style !== "none" && style !== "hidden" && width > 0;
  return {
    width: visible ? String(width) : "1",
    style: visible ? style : "solid",
    color: cssColorToHex(computed.borderTopColor, DEFAULT_BORDER_COLOR),
  };
}

/**
 * Gesetzte Größe eines Elements aus Inline-Style oder width/height-Attribut.
 * Gemessene Werte bleiben außen vor: sonst würde ein bloßes Öffnen des Dialogs
 * die tatsächliche Pixelbreite festschreiben.
 */
function readSize(el: HTMLElement, property: "width" | "height"): string {
  const inline = property === "width" ? el.style.width : el.style.height;
  if (inline) return inline.trim();
  const attr = el.getAttribute(property);
  if (!attr) return "";
  return /^\d+$/.test(attr.trim()) ? `${attr.trim()}px` : attr.trim();
}

/**
 * Entfernt eine Inline-CSS-Eigenschaft in allen Nachfahren, damit die Vorgabe
 * der Zelle sichtbar wird (sonst gewinnt z. B. ein span mit eigener Farbe).
 */
function clearDescendantStyle(cell: HTMLElement, property: string): void {
  cell.querySelectorAll<HTMLElement>("*").forEach((el) => {
    el.style.removeProperty(property);
    if (property === "color") el.removeAttribute("color");
    if (property === "font-family") el.removeAttribute("face");
    if (property === "font-size") el.removeAttribute("size");
  });
}

/** Ersetzt Auszeichnungs-Elemente durch ihren Inhalt, z. B. <b> beim Abwählen von Fett. */
function unwrapDescendants(cell: HTMLElement, selector: string): void {
  cell.querySelectorAll(selector).forEach((el) => el.replaceWith(...Array.from(el.childNodes)));
}

/** Beschriftete Zeile; `hint` ergänzt ein Sternchen mit Hovertext. */
function labelledRow(labelText: string, control: HTMLElement, hint?: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "sunnysign-table-dialog__row";
  const label = document.createElement("span");
  label.className = "sunnysign-table-dialog__label";
  label.textContent = labelText;
  if (hint) {
    const marker = document.createElement("span");
    marker.className = "sunnysign-table-dialog__hint";
    marker.textContent = "*";
    marker.title = hint;
    label.appendChild(marker);
  }
  row.appendChild(label);
  row.appendChild(control);
  return row;
}

/**
 * Größenfeld wie im Bild-Dialog: Zahlenfeld plus Auswahl der Einheit.
 * `get` liefert "" für leer (= automatisch) und null bei ungültiger Zahl.
 */
interface SizeControl {
  element: HTMLElement;
  input: HTMLInputElement;
  unit: HTMLSelectElement;
  get: () => string | null;
  set: (value: string) => void;
  setDisabled: (disabled: boolean) => void;
  setTitle: (title: string) => void;
}

function createSizeControl(value: string, ariaLabel: string, unitAriaLabel: string): SizeControl {
  const row = document.createElement("div");
  row.className = "sunnysign-table-dialog__size";
  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "numeric";
  input.className = "sunnysign-input sunnysign-table-dialog__number";
  input.setAttribute("aria-label", ariaLabel);
  input.addEventListener("input", () => {
    const cleaned = input.value.replace(/[^\d]/g, "");
    if (input.value !== cleaned) input.value = cleaned;
  });
  const unit = createSelectControl(
    [
      { label: "px", value: "px" },
      { label: "%", value: "%" },
    ],
    "px",
    unitAriaLabel
  );
  unit.classList.add("sunnysign-table-dialog__unit");
  row.appendChild(input);
  row.appendChild(unit);

  const control: SizeControl = {
    element: row,
    input,
    unit,
    get: () => (input.value.trim() ? normalizeSizeInput(`${input.value.trim()}${unit.value}`) : ""),
    set: (raw) => {
      const match = /^(\d+)\s*(px|%)?$/.exec((raw ?? "").trim().toLowerCase());
      input.value = match ? match[1] : "";
      unit.value = match && match[2] === "%" ? "%" : "px";
    },
    setDisabled: (disabled) => {
      input.disabled = disabled;
      unit.disabled = disabled;
    },
    setTitle: (title) => {
      input.title = title;
      unit.title = title;
    },
  };
  control.set(value);
  return control;
}

function createDialogError(): HTMLParagraphElement {
  const el = document.createElement("p");
  el.className = "sunnysign-editor-dialog-error";
  el.setAttribute("role", "alert");
  el.hidden = true;
  return el;
}

function showDialogError(el: HTMLElement, message: string): void {
  el.textContent = message;
  el.hidden = false;
}

/** Versteckt die Meldung, sobald der Wert erneut bearbeitet wird. */
function clearErrorOnInput(el: HTMLElement, fields: HTMLInputElement[]): void {
  fields.forEach((field) =>
    field.addEventListener("input", () => {
      el.textContent = "";
      el.hidden = true;
    })
  );
}

function createNumberInput(value: string, ariaLabel: string): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "numeric";
  input.className = "sunnysign-input sunnysign-table-dialog__number";
  input.value = value;
  input.setAttribute("aria-label", ariaLabel);
  input.addEventListener("input", () => {
    const cleaned = input.value.replace(/[^\d]/g, "");
    if (input.value !== cleaned) input.value = cleaned;
  });
  return input;
}

function createSelectControl(
  options: { label: string; value: string }[],
  value: string,
  ariaLabel: string
): HTMLSelectElement {
  const select = document.createElement("select");
  select.className = "sunnysign-editor-select sunnysign-table-dialog__select";
  select.setAttribute("aria-label", ariaLabel);
  options.forEach((o) => {
    const opt = document.createElement("option");
    opt.value = o.value;
    opt.textContent = o.label;
    select.appendChild(opt);
  });
  select.value = value;
  return select;
}

/**
 * Schriftart-Auswahl wie in der Editor-Toolbar: Liste mit Suchfeld, jeder
 * Eintrag in seiner eigenen Schrift. Ein leerer Wert steht für „Standard“.
 */
function createFontControl(
  deps: TableEditorDeps,
  initial: string
): { element: HTMLElement; get: () => string; set: (value: string) => void } {
  const fallback = deps.fontOptions[0];
  const labelFor = (value: string): string =>
    deps.fontOptions.find((o) => o.value === value)?.label ?? fallback?.label ?? "";
  let current = initial;

  const dropdown = createSearchableDropdown({
    title: deps.t("table.font"),
    searchPlaceholder: deps.t("editor.fontSearch"),
    extraClass: "sunnysign-table-dialog__fontlist",
    triggerLabel: labelFor(initial),
    options: deps.fontOptions.map((o) => ({
      value: o.value,
      label: o.label,
      previewFont: o.value || undefined,
    })),
    onPick: (value) => set(value),
  });

  const set = (value: string): void => {
    current = value;
    dropdown.setTriggerLabel(labelFor(value));
    dropdown.setActiveOption((optionValue) => optionValue === value);
  };
  set(initial);

  return { element: dropdown.element, get: () => current, set };
}

/** Farb-Button, der den Editor-Farbwähler öffnet und den gewählten Wert merkt. */
function createColorButton(
  deps: TableEditorDeps,
  titleKey: string,
  initial: string,
  isBackground: boolean
): { element: HTMLButtonElement; get: () => string; set: (v: string) => void } {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sunnysign-table-dialog__color";
  let current = initial;
  const render = (): void => {
    const parsed = parseCssColor(current);
    const alpha = parsed ? parsed.a : 1;
    const hex = cssColorToHex(current, "#ffffff");
    button.style.setProperty("--current-color", alpha === 0 ? "transparent" : current);
    button.classList.toggle("sunnysign-table-dialog__color--none", alpha === 0);
    button.textContent =
      alpha === 0 ? deps.t("table.backgroundNone") : alpha < 1 ? `${hex} ${Math.round(alpha * 100)}%` : hex;
    button.title = current;
  };
  const set = (v: string): void => {
    current = v;
    render();
  };
  render();
  button.addEventListener("click", (e) => {
    e.preventDefault();
    deps.pickColor(titleKey, current, isBackground, (picked) => set(picked));
  });
  return { element: button, get: () => current, set };
}

/**
 * Dreistufiger Schalter je Kante: Linie setzen, Linie entfernen, unverändert lassen.
 * Start ist immer „unverändert“ – erst die bewusste Auswahl ändert etwas.
 * Kanten, die es im gewählten Bereich nicht gibt, bleiben sichtbar, aber gesperrt.
 */
function createEdgeToggle(
  t: (key: string) => string,
  disabled: boolean
): { element: HTMLButtonElement; get: () => EdgeState } {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sunnysign-table-dialog__edge";
  button.disabled = disabled;
  let state: EdgeState = "keep";
  const labels: Record<EdgeState, string> = {
    on: t("table.borderStateOn"),
    off: t("table.borderStateOff"),
    keep: t("table.borderStateKeep"),
  };
  const render = (): void => {
    button.classList.toggle("is-on", state === "on");
    button.classList.toggle("is-off", state === "off");
    button.classList.toggle("is-keep", state === "keep");
    button.textContent = labels[state];
    button.title = disabled ? t("table.edgeUnavailable") : t("table.borderStateHint");
    button.setAttribute("aria-label", labels[state]);
  };
  render();
  if (!disabled) {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      state = state === "keep" ? "on" : state === "on" ? "off" : "keep";
      render();
    });
  }
  return { element: button, get: () => (disabled ? "keep" : state) };
}

/**
 * Rahmenlinien im Detail – wie der Rahmen-Dialog in Word: Linienart, Farbe und
 * Breite wählen, dann je Kante (oben, unten, links, rechts, innen senkrecht,
 * innen waagerecht) setzen, entfernen oder unverändert lassen. Gilt für den
 * Bereich, der im Format-Dialog gewählt ist.
 */
function openBorderDetailsDialog(
  deps: TableEditorDeps,
  scope: TableScope,
  ctx: TableContext,
  preset: { width: string; style: string; color: string },
  onApplied: () => void
): void {
  const t = deps.t;
  const grid = scopeGrid(scope, ctx);
  const edges = availableEdges(grid);

  const overlay = document.createElement("div");
  overlay.className = "sunnysign-editor-dialog-overlay sunnysign-table-dialog";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const box = document.createElement("div");
  box.className = "sunnysign-editor-dialog-box sunnysign-table-dialog__box";

  const title = document.createElement("p");
  title.className = "sunnysign-editor-color-dialog__title";
  title.textContent = t("table.borderDetailsTitle");

  const scopeNote = document.createElement("p");
  scopeNote.className = "sunnysign-table-dialog__note";
  scopeNote.textContent = `${t("table.scope")}: ${t(
    scope === "cell"
      ? "table.scopeCell"
      : scope === "row"
        ? "table.scopeRow"
        : scope === "column"
          ? "table.scopeColumn"
          : "table.scopeTable"
  )}`;

  const hint = document.createElement("p");
  hint.className = "sunnysign-table-dialog__note";
  hint.textContent = t("table.borderDetailsHint");

  const widthInput = createNumberInput(preset.width, t("table.borderWidth"));
  const styleSelect = createSelectControl(
    BORDER_STYLES.filter((s) => s !== "none").map((s) => ({ label: t(`table.borderStyle.${s}`), value: s })),
    preset.style,
    t("table.borderStyle")
  );
  const color = createColorButton(deps, "table.borderColor", preset.color, false);
  const errorEl = createDialogError();
  clearErrorOnInput(errorEl, [widthInput]);

  const edgeLabels: Record<BorderEdge, string> = {
    top: t("table.edgeTop"),
    bottom: t("table.edgeBottom"),
    left: t("table.edgeLeft"),
    right: t("table.edgeRight"),
    insideVertical: t("table.edgeInsideVertical"),
    insideHorizontal: t("table.edgeInsideHorizontal"),
  };

  // Immer alle Kanten zeigen; im Bereich nicht vorhandene bleiben gesperrt sichtbar.
  const toggles = new Map<BorderEdge, { element: HTMLButtonElement; get: () => EdgeState }>();
  const rows = new Map<BorderEdge, HTMLElement>();
  for (const edge of ALL_BORDER_EDGES) {
    const toggle = createEdgeToggle(t, !edges.includes(edge));
    toggles.set(edge, toggle);
    rows.set(edge, labelledRow(edgeLabels[edge], toggle.element));
  }

  const actions = document.createElement("div");
  actions.className = "sunnysign-form-actions";
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "sunnysign-btn sunnysign-btn--primary";
  okBtn.textContent = t("editor.colorOk");
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "sunnysign-btn sunnysign-btn--secondary";
  cancelBtn.textContent = t("form.cancel");
  actions.appendChild(okBtn);
  actions.appendChild(cancelBtn);

  box.appendChild(title);
  box.appendChild(scopeNote);
  box.appendChild(labelledRow(t("table.borderStyle"), styleSelect));
  box.appendChild(labelledRow(t("table.borderColor"), color.element));
  box.appendChild(labelledRow(t("table.borderWidth"), widthInput));
  box.appendChild(hint);
  for (const edge of ALL_BORDER_EDGES) {
    const row = rows.get(edge);
    if (row) box.appendChild(row);
  }
  box.appendChild(errorEl);
  box.appendChild(actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();
  cancelBtn.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  okBtn.addEventListener("click", () => {
    const width = parseIntegerInput(widthInput.value, LIMITS.borderWidth.min, LIMITS.borderWidth.max);
    if (width === null) {
      showDialogError(errorEl, t("table.errorBorderWidth"));
      widthInput.focus();
      return;
    }
    const value = width === 0 ? "none" : `${width}px ${styleSelect.value} ${color.get()}`;
    for (const edge of ALL_BORDER_EDGES) {
      const state = toggles.get(edge)?.get() ?? "keep";
      if (state === "keep") continue;
      applyEdgeBorder(borderTargets(edge, grid), state === "on" ? value : "none");
    }
    close();
    onApplied();
  });
}

/**
 * Format-Dialog für Zelle, Zeile, Spalte oder Tabelle. Die Felder zeigen die
 * Werte der Cursorstelle bzw. der ersten Zelle des Bereichs; geändert wird das
 * HTML erst beim Bestätigen, und nur bei tatsächlich veränderten Feldern.
 */
export function openTableFormatDialog(deps: TableEditorDeps, initialScope: TableScope, ctx: TableContext): void {
  const t = deps.t;
  let scope: TableScope = initialScope;

  const overlay = document.createElement("div");
  overlay.className = "sunnysign-editor-dialog-overlay sunnysign-table-dialog";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const box = document.createElement("div");
  box.className = "sunnysign-editor-dialog-box sunnysign-table-dialog__box";

  const scopeSelect = createSelectControl(
    [
      { label: t("table.scopeCell"), value: "cell" },
      { label: t("table.scopeRow"), value: "row" },
      { label: t("table.scopeColumn"), value: "column" },
      { label: t("table.scopeTable"), value: "table" },
    ],
    scope,
    t("table.scope")
  );
  scopeSelect.classList.add("sunnysign-table-dialog__scope");

  // Der Bereich steht in der Überschrift: „Format“ plus Auswahl.
  const heading = document.createElement("div");
  heading.className = "sunnysign-table-dialog__heading";
  const headingText = document.createElement("p");
  headingText.className = "sunnysign-editor-color-dialog__title";
  headingText.textContent = t("table.formatHeading");
  heading.appendChild(headingText);
  heading.appendChild(scopeSelect);

  const initial = readFormat(scope, ctx, deps.fontOptions);
  const paddingInput = createNumberInput(initial.padding, t("table.padding"));
  // Kein Hintergrund heißt: Farbe mit 0 % Deckkraft. Das stellt der Farbdialog ein.
  const background = createColorButton(
    deps,
    "table.background",
    initial.backgroundSet ? initial.background : TRANSPARENT_COLOR,
    true
  );
  const textColor = createColorButton(deps, "table.textColor", initial.color, false);
  const fontControl = createFontControl(deps, initial.fontFamily);
  const fontSizeInput = createNumberInput(initial.fontSize, t("table.fontSize"));
  const createStyleCheckbox = (checked: boolean, labelKey: string): HTMLInputElement => {
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "sunnysign-table-dialog__checkbox";
    box.checked = checked;
    box.setAttribute("aria-label", t(labelKey));
    return box;
  };
  const boldCheckbox = createStyleCheckbox(initial.bold, "table.bold");
  const italicCheckbox = createStyleCheckbox(initial.italic, "table.italic");
  const underlineCheckbox = createStyleCheckbox(initial.underline, "table.underline");
  const alignSelect = createSelectControl(
    [
      { label: t("editor.alignLeft"), value: "left" },
      { label: t("editor.alignCenter"), value: "center" },
      { label: t("editor.alignRight"), value: "right" },
    ],
    initial.textAlign,
    t("table.align")
  );
  const valignSelect = createSelectControl(
    [
      { label: t("table.valignTop"), value: "top" },
      { label: t("table.valignMiddle"), value: "middle" },
      { label: t("table.valignBottom"), value: "bottom" },
    ],
    initial.verticalAlign,
    t("table.valign")
  );
  const widthControl = createSizeControl(initial.width, t("table.width"), t("table.widthUnit"));
  const heightControl = createSizeControl(initial.height, t("table.height"), t("table.heightUnit"));
  // Die Felder sind schmal; der Hinweis steht im Tooltip und am Sternchen des Labels.
  heightControl.setTitle(t("table.heightAuto"));
  const errorEl = createDialogError();

  /** Vorbelegung der Felder aus dem aktuell gewählten Bereich (ändert nichts am HTML). */
  let snapshot = initial;
  const prefill = (): void => {
    const measured = readFormat(scope, ctx, deps.fontOptions);
    // Nur Größen anzeigen, die das Feld auch annimmt; sonst würde ein Wert wie
    // "auto" beim Bestätigen als Fehler gelten, obwohl niemand ihn eingegeben hat.
    snapshot = {
      ...measured,
      width: normalizeSizeInput(measured.width) ?? "",
      height: normalizeSizeInput(measured.height) ?? "",
    };
    paddingInput.value = snapshot.padding;
    background.set(snapshot.backgroundSet ? snapshot.background : TRANSPARENT_COLOR);
    textColor.set(snapshot.color);
    fontControl.set(snapshot.fontFamily);
    fontSizeInput.value = snapshot.fontSize;
    boldCheckbox.checked = snapshot.bold;
    italicCheckbox.checked = snapshot.italic;
    underlineCheckbox.checked = snapshot.underline;
    alignSelect.value = snapshot.textAlign;
    valignSelect.value = snapshot.verticalAlign;
    widthControl.set(snapshot.width);
    heightControl.set(snapshot.height);
    widthControl.setDisabled(scope === "row");
    widthControl.setTitle(scope === "row" ? t("table.widthRowHint") : t("table.widthHint"));
  };
  prefill();

  scopeSelect.addEventListener("change", () => {
    scope = scopeSelect.value as TableScope;
    prefill();
  });

  const borderDetailsBtn = document.createElement("button");
  borderDetailsBtn.type = "button";
  borderDetailsBtn.className = "sunnysign-btn sunnysign-btn--secondary sunnysign-table-dialog__details";
  borderDetailsBtn.textContent = t("table.borderDetails");
  borderDetailsBtn.title = t("table.borderDetailsHint");
  borderDetailsBtn.addEventListener("click", (e) => {
    e.preventDefault();
    openBorderDetailsDialog(deps, scope, ctx, readBorderPreset(scope, ctx), () => {
      prefill();
      deps.notifyChange();
    });
  });

  const actions = document.createElement("div");
  actions.className = "sunnysign-form-actions";
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "sunnysign-btn sunnysign-btn--primary";
  okBtn.textContent = t("editor.colorOk");
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "sunnysign-btn sunnysign-btn--secondary";
  cancelBtn.textContent = t("form.cancel");
  actions.appendChild(okBtn);
  actions.appendChild(cancelBtn);

  box.appendChild(heading);
  box.appendChild(labelledRow(t("table.borderEdges"), borderDetailsBtn));
  box.appendChild(labelledRow(t("table.padding"), paddingInput));
  box.appendChild(labelledRow(t("table.background"), background.element));
  box.appendChild(labelledRow(t("table.textColor"), textColor.element));
  box.appendChild(labelledRow(t("table.font"), fontControl.element));
  box.appendChild(labelledRow(t("table.fontSize"), fontSizeInput));
  box.appendChild(labelledRow(t("table.bold"), boldCheckbox));
  box.appendChild(labelledRow(t("table.italic"), italicCheckbox));
  box.appendChild(labelledRow(t("table.underline"), underlineCheckbox));
  box.appendChild(labelledRow(t("table.align"), alignSelect));
  box.appendChild(labelledRow(t("table.valign"), valignSelect));
  box.appendChild(labelledRow(t("table.width"), widthControl.element));
  box.appendChild(labelledRow(t("table.height"), heightControl.element, t("table.heightAuto")));
  clearErrorOnInput(errorEl, [paddingInput, fontSizeInput, widthControl.input, heightControl.input]);
  box.appendChild(errorEl);
  box.appendChild(actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();
  cancelBtn.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  okBtn.addEventListener("click", () => {
    const padding = parseIntegerInput(paddingInput.value, LIMITS.padding.min, LIMITS.padding.max);
    const fontSize = parseIntegerInput(fontSizeInput.value, LIMITS.fontSize.min, LIMITS.fontSize.max);
    const width = widthControl.input.disabled ? "" : widthControl.get();
    const height = heightControl.get();
    const problem =
      padding === null
        ? { key: "table.errorPadding", field: paddingInput }
        : fontSize === null
          ? { key: "table.errorFontSize", field: fontSizeInput }
          : width === null
            ? { key: "table.errorWidth", field: widthControl.input }
            : height === null
              ? { key: "table.errorHeight", field: heightControl.input }
              : null;
    if (problem) {
      showDialogError(errorEl, t(problem.key));
      problem.field.focus();
      return;
    }
    const pickedBackground = background.get();
    const pickedAlpha = parseCssColor(pickedBackground)?.a ?? 1;
    applyTableFormat(scope, ctx, snapshot, {
      padding: String(padding),
      background: pickedBackground,
      backgroundSet: pickedAlpha > 0,
      color: textColor.get(),
      fontFamily: fontControl.get(),
      fontSize: String(fontSize),
      bold: boldCheckbox.checked,
      italic: italicCheckbox.checked,
      underline: underlineCheckbox.checked,
      textAlign: alignSelect.value,
      verticalAlign: valignSelect.value,
      width: width as string,
      height: height as string,
    });
    close();
    deps.notifyChange();
  });
}

/** Wert der Schriftart-Auswahl, der zur gemessenen font-family passt. */
function matchFontOption(options: { label: string; value: string }[], fontFamily: string): string {
  const key = (fontFamily.split(",")[0] ?? "").replace(/["']/g, "").trim().toLowerCase();
  const match = options.find((o) => o.value && (o.value.split(",")[0] ?? "").replace(/["']/g, "").trim().toLowerCase() === key);
  return match ? match.value : "";
}

/** Größenangabe normalisieren: "80" → "80px", "80%" bleibt, leer = entfernen. */
function normalizeSize(raw: string): string {
  const v = raw.trim();
  if (!v) return "";
  if (/^\d+%$/.test(v)) return v;
  const digits = v.replace(/[^\d]/g, "");
  return digits ? `${digits}px` : "";
}

/**
 * Setzt Breite oder Höhe als CSS und zusätzlich als HTML-Attribut — Outlook
 * Desktop richtet sich häufig nach dem Attribut. Leerer Wert entfernt beides.
 */
function applySize(targets: HTMLElement[], property: "width" | "height", raw: string): void {
  const value = normalizeSize(raw);
  for (const target of targets) {
    if (!value) {
      target.style.removeProperty(property);
      target.removeAttribute(property);
      continue;
    }
    target.style.setProperty(property, value);
    target.setAttribute(property, value.endsWith("px") ? value.slice(0, -2) : value);
  }
}

/** Wendet die im Dialog geänderten Felder auf den gewählten Bereich an. */
function applyTableFormat(
  scope: TableScope,
  ctx: TableContext,
  before: TableFormatValues,
  next: TableFormatValues
): void {
  const cells = collectScopeCells(scope, ctx);

  for (const cell of cells) {
    if (next.padding !== before.padding) {
      cell.style.padding = `${Math.max(0, Math.min(60, parseInt(next.padding, 10) || 0))}px`;
    }
    if (next.background !== before.background || next.backgroundSet !== before.backgroundSet) {
      if (!next.backgroundSet) {
        cell.style.removeProperty("background-color");
        cell.removeAttribute("bgcolor");
      } else {
        cell.style.backgroundColor = next.background;
        // bgcolor versteht nur Hex; bei rgba() bleibt es beim CSS.
        if (/^#[0-9a-f]{6}$/i.test(next.background)) cell.setAttribute("bgcolor", next.background);
        else cell.removeAttribute("bgcolor");
      }
    }
    if (next.color !== before.color) {
      cell.style.color = next.color;
      clearDescendantStyle(cell, "color");
    }
    if (next.fontFamily !== before.fontFamily) {
      if (next.fontFamily) cell.style.fontFamily = next.fontFamily;
      else cell.style.removeProperty("font-family");
      clearDescendantStyle(cell, "font-family");
    }
    if (next.fontSize !== before.fontSize) {
      const size = Math.max(1, Math.min(200, parseInt(next.fontSize, 10) || 0));
      if (size) cell.style.fontSize = `${size}px`;
      clearDescendantStyle(cell, "font-size");
    }
    if (next.bold !== before.bold) {
      if (next.bold) cell.style.fontWeight = "bold";
      else {
        cell.style.removeProperty("font-weight");
        clearDescendantStyle(cell, "font-weight");
        unwrapDescendants(cell, "b,strong");
      }
    }
    if (next.italic !== before.italic) {
      if (next.italic) cell.style.fontStyle = "italic";
      else {
        cell.style.removeProperty("font-style");
        clearDescendantStyle(cell, "font-style");
        unwrapDescendants(cell, "i,em");
      }
    }
    if (next.underline !== before.underline) {
      if (next.underline) cell.style.textDecoration = "underline";
      else {
        cell.style.removeProperty("text-decoration");
        clearDescendantStyle(cell, "text-decoration");
        clearDescendantStyle(cell, "text-decoration-line");
        // Links behalten ihre Unterstreichung; nur reine Auszeichnungen werden entfernt.
        unwrapDescendants(cell, "u,ins");
      }
    }
    if (next.textAlign !== before.textAlign) {
      cell.style.textAlign = next.textAlign;
      cell.setAttribute("align", next.textAlign);
    }
    if (next.verticalAlign !== before.verticalAlign) {
      cell.style.verticalAlign = next.verticalAlign;
      cell.setAttribute("valign", next.verticalAlign);
    }
  }

  if (next.width !== before.width) {
    applySize(collectWidthTargets(scope, ctx), "width", next.width);
  }
  if (next.height !== before.height) {
    applySize(collectHeightTargets(scope, ctx), "height", next.height);
  }

  if (scope === "table") ctx.table.style.borderCollapse = "collapse";
}
